import { useParams, Link, Navigate } from "react-router-dom";
import { getService, SERVICES } from "@/data/services";
import { ArrowRight, Check, ArrowUpRight } from "lucide-react";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <Navigate to="/" replace />;
  const Icon = service.icon;

  return (
    <div>
      {/* Hero */}
      <section className="border-b border-brand-border bg-brand-surface/40">
        <div className="container-x pt-20 pb-16 lg:pt-28 lg:pb-24">
          <Link to="/pricing" className="text-sm text-brand-muted hover:text-brand-ink inline-flex items-center gap-1 mb-8" data-testid="service-back-link">← All Services</Link>
          <div className="flex items-start gap-6">
            <div className="w-14 h-14 rounded-2xl bg-brand-ink text-white flex items-center justify-center shrink-0">
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <div className="eyebrow mb-2">Service</div>
              <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight text-brand-ink" data-testid="service-title">{service.title}</h1>
              <p className="mt-4 text-xl text-brand-muted max-w-2xl">{service.tagline}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="container-x py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3">Overview</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">What you get</h2>
            <p className="mt-5 text-brand-muted leading-relaxed">{service.summary}</p>
            <Link to="/contact" data-testid="service-cta-contact" className="btn-primary mt-8">Discuss This Service <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {service.features.map((f) => (
              <div key={f} className="flex items-start gap-3 p-5 bg-white border border-brand-border rounded-xl">
                <span className="w-6 h-6 rounded-full bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3.5 h-3.5" />
                </span>
                <span className="text-sm text-brand-ink font-medium">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-brand-surface border-y border-brand-border">
        <div className="container-x py-20 lg:py-24">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-3">Our Process</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">How we deliver</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {service.process.map((step, i) => (
              <div key={step} className="p-6 bg-white border border-brand-border rounded-2xl relative">
                <div className="font-mono text-xs text-brand-muted mb-4">Step {String(i + 1).padStart(2, "0")}</div>
                <div className="font-display font-bold text-xl text-brand-ink">{step}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing tiers */}
      <section className="container-x py-20 lg:py-24">
        <div className="max-w-2xl mb-12">
          <div className="eyebrow mb-3">Pricing</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Simple, transparent tiers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {service.tiers.map((t) => (
            <div key={t.name} data-testid={`service-tier-${t.name.toLowerCase()}`}
              className={`p-8 rounded-2xl border ${t.featured ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-brand-border"}`}>
              {t.featured && <div className="inline-block text-xs px-2 py-1 rounded-full bg-brand-blue text-white mb-4">Most Popular</div>}
              <div className={`font-display font-bold text-2xl ${t.featured ? "text-white" : "text-brand-ink"}`}>{t.name}</div>
              <div className="mt-1 text-sm opacity-70">{t.desc}</div>
              <div className="mt-6 flex items-baseline gap-1">
                <span className={`font-display font-black text-4xl ${t.featured ? "text-white" : "text-brand-ink"}`}>{t.price}</span>
                <span className="text-sm opacity-70">{t.per}</span>
              </div>
              <ul className="mt-6 space-y-2.5">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.featured ? "text-brand-blue" : "text-brand-blue"}`} />
                    <span className={t.featured ? "text-white/80" : "text-brand-muted"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" data-testid={`service-tier-cta-${t.name.toLowerCase()}`}
                className={`mt-8 w-full inline-flex items-center justify-center gap-1 px-5 py-3 rounded-xl font-medium transition-colors ${t.featured ? "bg-white text-brand-ink hover:bg-white/90" : "bg-brand-ink text-white hover:bg-black"}`}>
                Get Started <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Related */}
      <section className="container-x pb-24">
        <div className="text-xs uppercase tracking-widest text-brand-muted mb-6">Explore other services</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {SERVICES.filter((s) => s.slug !== service.slug).slice(0, 4).map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`related-service-${s.slug}`} className="p-5 bg-white border border-brand-border rounded-xl card-hover">
              <s.icon className="w-4 h-4 text-brand-blue mb-3" />
              <div className="font-display font-bold text-brand-ink">{s.title}</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
